/** @module @liquidefy/code-editor/plugins/plugins-lifecycle @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  PluginId,
  PluginManifest,
  PluginState,
  PluginStatus,
  PluginPermission,
  PluginContributions,
  PluginResourceUsage,
  CommandData,
  KeybindingData,
  ThemeContribution,
  LanguageContribution,
  SnippetContribution,
  SandboxResourceLimits,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asPluginId,
  now,
  emptyPluginState,
  DEFAULT_SANDBOX_RESOURCE_LIMITS,
} from '../code-editor.js';

// ═══ §2 PLUGIN CREATION — createPluginState, validateManifest, normalizeManifest, manifestToString ═══

export function createPluginState(id: string, manifest: PluginManifest): EditorResult<PluginState> {
  const validation = validateManifest(manifest);
  if (!validation.success) return validation as unknown as EditorResult<PluginState>;
  const pluginId = asPluginId(id);
  const state = emptyPluginState(pluginId, manifest);
  return ok(state, 'PLUGIN_CREATED', `Plugin "${manifest.name}" created`);
}

export function validateManifest(manifest: PluginManifest): EditorResult<boolean> {
  if (!manifest.name || typeof manifest.name !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "name" string');
  }
  if (!manifest.version || typeof manifest.version !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "version" string');
  }
  if (!/^\d+\.\d+\.\d+/.test(manifest.version)) {
    return err('INVALID_MANIFEST', `Manifest version "${manifest.version}" is not valid semver`);
  }
  if (!manifest.main || typeof manifest.main !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "main" entry point');
  }
  if (!manifest.displayName || typeof manifest.displayName !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "displayName"');
  }
  if (!manifest.description || typeof manifest.description !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "description"');
  }
  if (!manifest.author || typeof manifest.author !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "author"');
  }
  if (!manifest.license || typeof manifest.license !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "license"');
  }
  if (!Array.isArray(manifest.permissions)) {
    return err('INVALID_MANIFEST', 'Manifest "permissions" must be an array');
  }
  const validPerms: PluginPermission[] = [
    'fs.read', 'fs.write', 'network', 'process', 'clipboard',
    'storage', 'ui.notifications', 'ui.webview', 'terminal', 'git', 'debug', 'ai',
  ];
  for (const perm of manifest.permissions) {
    if (!validPerms.includes(perm)) {
      return err('INVALID_MANIFEST', `Unknown permission "${perm}"`);
    }
  }
  if (!Array.isArray(manifest.activationEvents)) {
    return err('INVALID_MANIFEST', 'Manifest "activationEvents" must be an array');
  }
  if (!manifest.contributes || typeof manifest.contributes !== 'object') {
    return err('INVALID_MANIFEST', 'Manifest must have a "contributes" object');
  }
  if (!manifest.dependencies || typeof manifest.dependencies !== 'object') {
    return err('INVALID_MANIFEST', 'Manifest must have a "dependencies" object');
  }
  return ok(true, 'MANIFEST_VALID', 'Manifest is valid');
}

export function normalizeManifest(manifest: Partial<PluginManifest> & { name: string; version: string; main: string }): PluginManifest {
  return {
    id: manifest.id ?? (generateId('manifest-') as unknown as PluginManifest['id']),
    name: manifest.name,
    version: manifest.version,
    displayName: manifest.displayName ?? manifest.name,
    description: manifest.description ?? '',
    author: manifest.author ?? 'unknown',
    license: manifest.license ?? 'MIT',
    repository: manifest.repository,
    main: manifest.main,
    activationEvents: manifest.activationEvents ?? ['*'],
    contributes: manifest.contributes ?? {
      commands: [],
      menus: [],
      keybindings: [],
      views: [],
      themes: [],
      languages: [],
      snippets: [],
      grammars: [],
      debuggers: [],
      formatters: [],
      settings: [],
    },
    permissions: manifest.permissions ?? [],
    dependencies: manifest.dependencies ?? {},
  };
}

export function manifestToString(manifest: PluginManifest): string {
  const permStr = manifest.permissions.length > 0
    ? manifest.permissions.join(', ')
    : 'none';
  const depCount = Object.keys(manifest.dependencies).length;
  const evtCount = manifest.activationEvents.length;
  return `${manifest.displayName} v${manifest.version} by ${manifest.author} [perms: ${permStr}] [deps: ${depCount}] [events: ${evtCount}]`;
}

// ═══ §3 STATUS TRANSITIONS — transitionPlugin, activatePlugin, deactivatePlugin, disablePlugin, enablePlugin, errorPlugin ═══

export const VALID_PLUGIN_TRANSITIONS: Record<PluginStatus, PluginStatus[]> = {
  'discovered': ['installed', 'error'],
  'installed': ['activating', 'disabled', 'uninstalling', 'error'],
  'activating': ['active', 'error'],
  'active': ['deactivating', 'disabled', 'updating', 'error'],
  'deactivating': ['installed', 'disabled', 'error'],
  'disabled': ['installed', 'activating', 'uninstalling', 'error'],
  'error': ['installed', 'activating', 'disabled', 'uninstalling'],
  'updating': ['active', 'installed', 'error'],
  'uninstalling': ['discovered', 'error'],
};

export function transitionPlugin(state: PluginState, newStatus: PluginStatus): EditorResult<PluginState> {
  const valid = VALID_PLUGIN_TRANSITIONS[state.status];
  if (!valid || !valid.includes(newStatus)) {
    return err(
      'INVALID_TRANSITION',
      `Cannot transition plugin "${state.manifest.name}" from "${state.status}" to "${newStatus}"`
    );
  }
  return ok(
    { ...state, status: newStatus },
    'PLUGIN_TRANSITION',
    `Plugin "${state.manifest.name}" transitioned from "${state.status}" to "${newStatus}"`
  );
}

export function activatePlugin(state: PluginState): EditorResult<PluginState> {
  if (state.status === 'active') {
    return ok(state, 'PLUGIN_ALREADY_ACTIVE', `Plugin "${state.manifest.name}" is already active`);
  }
  if (state.status !== 'installed' && state.status !== 'disabled' && state.status !== 'error') {
    const toActivating = transitionPlugin(state, 'activating');
    if (!toActivating.success) return toActivating;
    return transitionPlugin(toActivating.value, 'active');
  }
  const intermediate: PluginState = { ...state, status: 'activating' };
  return ok(
    { ...intermediate, status: 'active' as PluginStatus },
    'PLUGIN_ACTIVATED',
    `Plugin "${state.manifest.name}" activated`
  );
}

export function deactivatePlugin(state: PluginState): EditorResult<PluginState> {
  if (state.status !== 'active') {
    return err('INVALID_TRANSITION', `Cannot deactivate plugin "${state.manifest.name}" — status is "${state.status}"`);
  }
  return ok(
    { ...state, status: 'installed' as PluginStatus },
    'PLUGIN_DEACTIVATED',
    `Plugin "${state.manifest.name}" deactivated`
  );
}

export function disablePlugin(state: PluginState): EditorResult<PluginState> {
  if (state.status === 'disabled') {
    return ok(state, 'PLUGIN_ALREADY_DISABLED', `Plugin "${state.manifest.name}" is already disabled`);
  }
  if (state.status === 'active') {
    const deactivated = deactivatePlugin(state);
    if (!deactivated.success) return deactivated;
    return ok(
      { ...deactivated.value, status: 'disabled' as PluginStatus },
      'PLUGIN_DISABLED',
      `Plugin "${state.manifest.name}" disabled`
    );
  }
  return transitionPlugin(state, 'disabled');
}

export function enablePlugin(state: PluginState): EditorResult<PluginState> {
  if (state.status !== 'disabled') {
    return err('INVALID_TRANSITION', `Cannot enable plugin "${state.manifest.name}" — status is "${state.status}"`);
  }
  return ok(
    { ...state, status: 'installed' as PluginStatus },
    'PLUGIN_ENABLED',
    `Plugin "${state.manifest.name}" enabled`
  );
}

export function errorPlugin(state: PluginState, error?: string): EditorResult<PluginState> {
  return ok(
    { ...state, status: 'error' as PluginStatus },
    'PLUGIN_ERROR',
    error ?? `Plugin "${state.manifest.name}" encountered an error`
  );
}

export function isPluginActive(state: PluginState): boolean {
  return state.status === 'active';
}

export function isPluginDisabled(state: PluginState): boolean {
  return state.status === 'disabled';
}

export function isPluginError(state: PluginState): boolean {
  return state.status === 'error';
}

export function canActivate(state: PluginState): boolean {
  const valid = VALID_PLUGIN_TRANSITIONS[state.status];
  return !!valid && (valid.includes('activating') || valid.includes('active'));
}

export function pluginStatusLabel(status: PluginStatus): string {
  const labels: Record<PluginStatus, string> = {
    'discovered': 'Discovered',
    'installed': 'Installed',
    'activating': 'Activating',
    'active': 'Active',
    'deactivating': 'Deactivating',
    'disabled': 'Disabled',
    'error': 'Error',
    'updating': 'Updating',
    'uninstalling': 'Uninstalling',
  };
  return labels[status] ?? 'Unknown';
}

// ═══ §4 DEPENDENCY RESOLUTION — resolvePluginDependencies, checkDependencies, dependencyGraph, activationOrder ═══

export function resolvePluginDependencies(
  plugins: ReadonlyArray<PluginState>
): EditorResult<PluginState[]> {
  const cyclic = findCyclicDeps(plugins);
  if (cyclic.length > 0) {
    return err('CYCLIC_DEPENDENCY', `Cyclic dependency detected: ${cyclic.join(' -> ')}`);
  }
  const order = activationOrder(plugins);
  return ok(order, 'DEPS_RESOLVED', `Resolved activation order for ${order.length} plugins`);
}

export function checkDependencies(
  plugin: PluginState,
  available: ReadonlyArray<PluginState>
): { satisfied: boolean; missing: string[] } {
  const depNames = Object.keys(plugin.manifest.dependencies);
  const availableNames = new Set(available.map(p => p.manifest.name));
  const missing = depNames.filter(d => !availableNames.has(d));
  return { satisfied: missing.length === 0, missing };
}

export function dependencyGraph(
  plugins: ReadonlyArray<PluginState>
): Map<string, string[]> {
  const graph = new Map<string, string[]>();
  for (const plugin of plugins) {
    graph.set(plugin.manifest.name, Object.keys(plugin.manifest.dependencies));
  }
  return graph;
}

export function activationOrder(plugins: ReadonlyArray<PluginState>): PluginState[] {
  const graph = dependencyGraph(plugins);
  const visited = new Set<string>();
  const sorted: string[] = [];

  function visit(name: string): void {
    if (visited.has(name)) return;
    visited.add(name);
    const deps = graph.get(name) ?? [];
    for (const dep of deps) {
      visit(dep);
    }
    sorted.push(name);
  }

  for (const plugin of plugins) {
    visit(plugin.manifest.name);
  }

  const pluginByName = new Map(plugins.map(p => [p.manifest.name, p]));
  const result: PluginState[] = [];
  for (const name of sorted) {
    const p = pluginByName.get(name);
    if (p) result.push(p);
  }
  return result;
}

export function hasCyclicDeps(plugins: ReadonlyArray<PluginState>): boolean {
  return findCyclicDeps(plugins).length > 0;
}

export function findCyclicDeps(plugins: ReadonlyArray<PluginState>): string[] {
  const graph = dependencyGraph(plugins);
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  const parent = new Map<string, string>();

  for (const name of graph.keys()) {
    color.set(name, WHITE);
  }

  function dfs(node: string): string[] {
    color.set(node, GRAY);
    const deps = graph.get(node) ?? [];
    for (const dep of deps) {
      if (!color.has(dep)) continue;
      if (color.get(dep) === GRAY) {
        const cycle: string[] = [dep, node];
        let cur = node;
        while (cur !== dep) {
          const p = parent.get(cur);
          if (!p || p === dep) break;
          cycle.push(p);
          cur = p;
        }
        cycle.push(dep);
        return cycle.reverse();
      }
      if (color.get(dep) === WHITE) {
        parent.set(dep, node);
        const result = dfs(dep);
        if (result.length > 0) return result;
      }
    }
    color.set(node, BLACK);
    return [];
  }

  for (const name of graph.keys()) {
    if (color.get(name) === WHITE) {
      const result = dfs(name);
      if (result.length > 0) return result;
    }
  }
  return [];
}

export function canDisable(
  plugin: PluginState,
  activePlugins: ReadonlyArray<PluginState>
): boolean {
  const pluginName = plugin.manifest.name;
  for (const other of activePlugins) {
    if (other.manifest.name === pluginName) continue;
    const deps = Object.keys(other.manifest.dependencies);
    if (deps.includes(pluginName) && other.status === 'active') {
      return false;
    }
  }
  return true;
}

// ═══ §5 ACTIVATION — matchesActivationEvent, shouldActivate, parseActivationEvent, isEagerActivation ═══

export const ACTIVATION_EVENT_TYPES: ReadonlyArray<string> = [
  'onLanguage',
  'onCommand',
  'onFileSystem',
  'onView',
  'onStartup',
  'onUri',
  '*',
];

export function parseActivationEvent(event: string): { type: string; value: string } {
  if (event === '*') return { type: '*', value: '' };
  const colonIndex = event.indexOf(':');
  if (colonIndex === -1) return { type: event, value: '' };
  return {
    type: event.substring(0, colonIndex),
    value: event.substring(colonIndex + 1),
  };
}

export function matchesActivationEvent(event: string, activationEvents: ReadonlyArray<string>): boolean {
  for (const trigger of activationEvents) {
    if (trigger === '*') return true;
    if (trigger === event) return true;
    const parsedTrigger = parseActivationEvent(trigger);
    const parsedEvent = parseActivationEvent(event);
    if (parsedTrigger.type === parsedEvent.type) {
      if (parsedTrigger.value === '' || parsedTrigger.value === parsedEvent.value) {
        return true;
      }
    }
  }
  return false;
}

export function shouldActivate(plugin: PluginState, event: string): boolean {
  if (plugin.status === 'active') return false;
  if (!canActivate(plugin)) return false;
  return matchesActivationEvent(event, plugin.activationEvents);
}

export function isEagerActivation(plugin: PluginState): boolean {
  return plugin.activationEvents.includes('*') || plugin.activationEvents.includes('onStartup');
}

// ═══ §6 CONTRIBUTIONS — mergeContributions, extractContributions, contributionCount, getContributed* ═══

export function mergeContributions(
  existing: PluginContributions,
  incoming: PluginContributions
): PluginContributions {
  return {
    commands: [...(existing.commands ?? []), ...(incoming.commands ?? [])],
    menus: [...(existing.menus ?? []), ...(incoming.menus ?? [])],
    keybindings: [...(existing.keybindings ?? []), ...(incoming.keybindings ?? [])],
    views: [...(existing.views ?? []), ...(incoming.views ?? [])],
    themes: [...(existing.themes ?? []), ...(incoming.themes ?? [])],
    languages: [...(existing.languages ?? []), ...(incoming.languages ?? [])],
    snippets: [...(existing.snippets ?? []), ...(incoming.snippets ?? [])],
    grammars: [...(existing.grammars ?? []), ...(incoming.grammars ?? [])],
    debuggers: [...(existing.debuggers ?? []), ...(incoming.debuggers ?? [])],
    formatters: [...(existing.formatters ?? []), ...(incoming.formatters ?? [])],
    settings: [...(existing.settings ?? []), ...(incoming.settings ?? [])],
  };
}

export function extractContributions(manifest: PluginManifest): PluginContributions {
  return manifest.contributes;
}

export function contributionCount(contributions: PluginContributions): number {
  return (
    (contributions.commands?.length ?? 0) +
    (contributions.menus?.length ?? 0) +
    (contributions.keybindings?.length ?? 0) +
    (contributions.views?.length ?? 0) +
    (contributions.themes?.length ?? 0) +
    (contributions.languages?.length ?? 0) +
    (contributions.snippets?.length ?? 0) +
    (contributions.grammars?.length ?? 0) +
    (contributions.debuggers?.length ?? 0) +
    (contributions.formatters?.length ?? 0) +
    (contributions.settings?.length ?? 0)
  );
}

export function hasContribution(contributions: PluginContributions, type: keyof PluginContributions): boolean {
  const arr = contributions[type];
  return Array.isArray(arr) && arr.length > 0;
}

export function getContributedCommands(contributions: PluginContributions): CommandData[] {
  return contributions.commands ?? [];
}

export function getContributedKeybindings(contributions: PluginContributions): KeybindingData[] {
  return contributions.keybindings ?? [];
}

export function getContributedThemes(contributions: PluginContributions): ThemeContribution[] {
  return contributions.themes ?? [];
}

export function getContributedLanguages(contributions: PluginContributions): LanguageContribution[] {
  return contributions.languages ?? [];
}

export function getContributedSnippets(contributions: PluginContributions): SnippetContribution[] {
  return contributions.snippets ?? [];
}

export function validateContributions(contributions: PluginContributions): EditorResult<boolean> {
  if (contributions.commands) {
    for (const cmd of contributions.commands) {
      if (!cmd.id || !cmd.title) {
        return err('INVALID_CONTRIBUTION', `Command contribution missing "id" or "title"`);
      }
    }
  }
  if (contributions.keybindings) {
    for (const kb of contributions.keybindings) {
      if (!kb.command) {
        return err('INVALID_CONTRIBUTION', 'Keybinding contribution missing "command"');
      }
    }
  }
  if (contributions.themes) {
    for (const theme of contributions.themes) {
      if (!theme.label || !theme.path) {
        return err('INVALID_CONTRIBUTION', 'Theme contribution missing "label" or "path"');
      }
    }
  }
  if (contributions.languages) {
    for (const lang of contributions.languages) {
      if (!lang.id) {
        return err('INVALID_CONTRIBUTION', 'Language contribution missing "id"');
      }
    }
  }
  if (contributions.snippets) {
    for (const snippet of contributions.snippets) {
      if (!snippet.language || !snippet.path) {
        return err('INVALID_CONTRIBUTION', 'Snippet contribution missing "language" or "path"');
      }
    }
  }
  if (contributions.grammars) {
    for (const grammar of contributions.grammars) {
      if (!grammar.scopeName || !grammar.path) {
        return err('INVALID_CONTRIBUTION', 'Grammar contribution missing "scopeName" or "path"');
      }
    }
  }
  if (contributions.debuggers) {
    for (const dbg of contributions.debuggers) {
      if (!dbg.type || !dbg.label) {
        return err('INVALID_CONTRIBUTION', 'Debugger contribution missing "type" or "label"');
      }
    }
  }
  if (contributions.formatters) {
    for (const fmt of contributions.formatters) {
      if (!fmt.language || !fmt.command) {
        return err('INVALID_CONTRIBUTION', 'Formatter contribution missing "language" or "command"');
      }
    }
  }
  if (contributions.settings) {
    for (const setting of contributions.settings) {
      if (!setting.title || !setting.properties) {
        return err('INVALID_CONTRIBUTION', 'Settings contribution missing "title" or "properties"');
      }
    }
  }
  return ok(true, 'CONTRIBUTIONS_VALID', 'All contributions are valid');
}

// ═══ §7 RESOURCE TRACKING — updateResourceUsage, isWithinLimits, exceedsMemory, resourceReport ═══

export const DEFAULT_RESOURCE_LIMITS: SandboxResourceLimits = DEFAULT_SANDBOX_RESOURCE_LIMITS;

export function updateResourceUsage(
  state: PluginState,
  usage: Partial<PluginResourceUsage>
): PluginState {
  return {
    ...state,
    resourceUsage: {
      cpuMs: usage.cpuMs ?? state.resourceUsage.cpuMs,
      memoryMb: usage.memoryMb ?? state.resourceUsage.memoryMb,
      activationTimeMs: usage.activationTimeMs ?? state.resourceUsage.activationTimeMs,
      apiCalls: usage.apiCalls ?? state.resourceUsage.apiCalls,
    },
  };
}

export function isWithinLimits(usage: PluginResourceUsage, limits: SandboxResourceLimits): boolean {
  return (
    usage.cpuMs <= limits.maxCpuMs &&
    usage.memoryMb <= limits.maxMemoryMb &&
    usage.apiCalls <= limits.maxApiCallsPerMinute
  );
}

export function exceedsMemory(usage: PluginResourceUsage, limitMb: number): boolean {
  return usage.memoryMb > limitMb;
}

export function exceedsCPU(usage: PluginResourceUsage, limitMs: number): boolean {
  return usage.cpuMs > limitMs;
}

export function exceedsAPICalls(usage: PluginResourceUsage, limit: number): boolean {
  return usage.apiCalls > limit;
}

export function resourceReport(state: PluginState): string {
  const u = state.resourceUsage;
  return `Plugin "${state.manifest.name}": CPU ${u.cpuMs}ms, Memory ${u.memoryMb}MB, Activation ${u.activationTimeMs}ms, API calls ${u.apiCalls}`;
}

export function shouldThrottle(usage: PluginResourceUsage, limits: SandboxResourceLimits): boolean {
  const cpuRatio = usage.cpuMs / limits.maxCpuMs;
  const memRatio = usage.memoryMb / limits.maxMemoryMb;
  const apiRatio = usage.apiCalls / limits.maxApiCallsPerMinute;
  return cpuRatio > 0.8 || memRatio > 0.8 || apiRatio > 0.8;
}
